import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Avatar, Box, Card, Divider, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MuiCheckbox,
  MuiDatePicker,
  MuiIconButton,
  MuiInput,
  MuiSearchSelect,
  MuiStack,
} from "..";
import { CloseIcon, EditProfileIcon, SaveIcon } from "../../assets/images";
import { ResponseObject } from "../../common/common.entity";
import { generateFormFields } from "../../common/commonFunctions";
import { APIParams } from "../../pages/userMangement/role/apiCall";
import { setReducerState } from "../../redux/crudSlice";
import { useAppSelector } from "../../store/hooks";
import { MuiAccordionArray } from "../MuiAccordionArray";
import { CircularLoader } from "../CircularLoader";
import { MuiLogo } from "../MuiLogo";
import HorizontalTree from "../HorizontalTree";
import { FormElementProps, FormItemComponent, FormItemTypes, IFormBuilder, IFormData, ValueTypes } from "./formBuilder.entity";
import { useDynamicForm } from "./useDynamicForm";

const handleInitialFValue = (fields: IFormBuilder['formData'], initialState: ResponseObject = {}) => {

  fields?.forEach((item) => {
    if (!item?.name) return

    switch (item.type) {
      case FormItemTypes.Container:
      case FormItemTypes.Card:
        handleInitialFValue(item.elements ?? [], initialState)
    }

    initialState[item?.name] = item?.value;
  });

  return initialState;
};


export const FormBuilderContainer = (props: IFormBuilder) => {
  const {
    createKey = '_create',
    createRequest,
    createFormAPI,

    updateKey = '_update',
    updateRequest,
    updateFormAPI,

    getKey = '_get',
    getRequest,
    getFormAPI,

    formData,
    headerLabel,
    isHeader = true,
    isRightMenu = false,
    onClose,
    onSubmit,
    isTitle = true,
    disableAPICalls = false,
    pagingAPIKey = '',
    setFieldProps,
    parentPath
  } = props;

  let { mode = 'add' } = props;

  const crud = useAppSelector((state) => state.crud);
  const createLoader = 'isLoading' + createKey;
  const updateLoader = 'isLoading' + updateKey;
  const getLoader = 'isLoading' + getKey;
  const isLoadingOnSubmit = crud[createLoader] === 'loading' || crud[updateLoader] === 'loading'
  const isLoadingOnGet = crud[getLoader] === 'loading'

  const [searchParams] = useSearchParams();
  if (!isRightMenu) mode = searchParams.get('mode') || mode;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isDisabled, setIsDisabled] = useState(!isRightMenu && mode !== 'add');

  const formFields = useMemo(() => generateFormFields(formData), []);
  const pagingUpdateKey = pagingAPIKey + 'Updated';

  const {
    values,
    setValues,
    errors,
    handleOptionChange,
    handleInputChange,
    handleServerSideErrors,
    formValidate,
  } = useDynamicForm(handleInitialFValue(formFields), true, null, formFields);

  useEffect(() => {
    if (mode !== 'add') {
      getData();
    }
  }, []);

  const getData = async () => {
    let resp: any = {};

    if (disableAPICalls || !getFormAPI) {
      resp = crud[getKey];
    }
    else {
      resp = await getFormAPI({ key: getKey || undefined, request: { ...getRequest } });
    }

    if (resp?.success) {
      setValues(resp?.data);
    } else {
      handleServerSideErrors(resp);
    }
    return resp;
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (formValidate(values, formFields)) {
      let newValues: ResponseObject = { ...values };
      if (onSubmit) onSubmit(newValues);

      formFields?.forEach((it: any) => {
        if (it.valueTypes === ValueTypes.number) {
          newValues[it.name] = parseInt(values[it.name]);
        }
        if (it.valueTypes === ValueTypes.decimalNumber) {
          newValues[it.name] = Number(values[it.name]);
        }
      });

      let res: ResponseObject | undefined;
      if (mode === 'add') {
        let request = { ...newValues, ...createRequest }
        if (createFormAPI) res = await createFormAPI({ key: createKey, request: request });
      }
      else {
        let request = { ...newValues, ...updateRequest }
        if (updateFormAPI) res = await updateFormAPI({ key: updateKey, request: request })
      }

      if (res?.success) {
        onCancel(e, true);
      } else {
        handleServerSideErrors(res);
      }
    }
  }

  const onCancel = (e?: any, isUpdated?: boolean) => {
    if (e) e.preventDefault();
    if (isUpdated) dispatch(setReducerState({ key: pagingUpdateKey, value: !!isUpdated }));

    if (isRightMenu) {
      if (onClose) onClose(e, isUpdated);
    } else if (parentPath) {
      navigate(parentPath);
    }
  };

  const ActionButtons = () => {
    return (
      <MuiStack>
        {(mode === "edit") && isDisabled && (
          <MuiIconButton
            onClick={() => setIsDisabled(false)}
            size="large"
            tooltipTitle={"Edit"}
          >
            <Avatar
              src={EditProfileIcon}
              sx={{
                width: 18,
                height: 18,
              }}
            />
          </MuiIconButton>
        )}

        {((mode === 'add') || !isDisabled) ? (
          <>
            <MuiIconButton
              onClick={handleSubmit}
              size="large"
              tooltipTitle={"Save"}
              loadingStatus={mode === 'edit' ? crud[updateLoader] : crud[createLoader]}
            >
              <Avatar
                src={SaveIcon}
                sx={{
                  width: 18,
                  height: 18,
                }}
              />
            </MuiIconButton>

            <MuiIconButton
              tooltipTitle={"Cancel"}
              onClick={
                (isRightMenu || mode === 'add')
                  ? onCancel
                  : () => setIsDisabled(true)
              }
              size="large"
            >
              <Avatar
                src={CloseIcon}
                sx={{
                  width: 18,
                  height: 18,
                }}
              />
            </MuiIconButton>
          </>
        ) : (isRightMenu &&
          <MuiIconButton
            tooltipTitle={"Cancel"}
            onClick={onCancel}
            size="large"
          >
            <Avatar
              src={CloseIcon}
              sx={{
                width: 18,
                height: 18,
              }}
            />
          </MuiIconButton>
        )}
      </MuiStack>
    );
  };

  return (
    <Box>
      {isHeader && (
        <>
          <MuiStack justifyContent="space-between" alignItems='center'>
            <MuiStack alignItems='center'>
              {!isRightMenu && (
                <MuiIconButton onClick={onCancel}>
                  <ArrowBackIcon fontSize="medium" />
                </MuiIconButton>
              )}
              {isTitle &&
                <Typography variant="h1">
                  {(` ${headerLabel || "Header"}`)}
                </Typography>
              }
            </MuiStack>

            <ActionButtons />
          </MuiStack>
          <Divider sx={{ opacity: 0.3, mb: 2 }} />
        </>
      )}

      <Grid container spacing={2} my={1}>

        {!isLoadingOnGet ? (
          <>
            {formData?.map(function formLoop(el: (IFormData | null)) {
              if (!el) return;

              const { itemSize = { xs: 12, sm: 6, md: 4, lg: 3 }, ...others } = el;

              let elementProps: FormElementProps = {
                ...el,
                value: values[el.name] || el.value,
                formLoop,
                onChange: (event: any, value?: any, reason?: any) => {
                  if (el.type === FormItemTypes.SearchSelect) handleOptionChange(event, { name: el.name, value: value ? value[el?.valueExpr || 'id'] : null }, reason);
                  else if (el.type === FormItemTypes.TreeList) handleInputChange({ target: { name: el.name } }, value);
                  else handleInputChange(event, value);
                  if (el?.onChange) el?.onChange(event, value, reason);
                },
                error: el.error || errors[el.name],
                disabled: el.disabled
                  || (mode !== 'add' && isDisabled)
                  || isLoadingOnSubmit,
              }

              if (setFieldProps) {
                let overrideProps = setFieldProps?.(elementProps.value, values, elementProps)
                if (overrideProps) elementProps = { ...elementProps, ...overrideProps }
              }

              if (elementProps.type === FormItemTypes.SearchSelect) {
                elementProps.getListData = (params: APIParams) => {
                  params.state = values
                  return el.getListData?.(params)
                }
              }

              return (
                el.type === FormItemTypes.Container ?
                  <Grid
                    container
                    size={itemSize}
                    {...others}
                  >
                    {el.elements.map(formLoop)}
                  </Grid>
                  :
                  <Grid
                    size={itemSize}
                  >
                    {FormElement[el.type](elementProps)}
                  </Grid>)
            })}
          </>
        ) : (
          <Grid size={12}>
            <MuiStack>
              <CircularLoader />
            </MuiStack>
          </Grid>
        )}
      </Grid>

    </Box>
  );
};

export const FormElement: Record<string, FormItemComponent> = {
  SearchSelect: (el) => (
    <MuiSearchSelect
      {...el}
      name={el.name}
      label={el.label && (el.label)}
      fullWidth
      options={el.options}
    />
  ),

  Logo: (el) =>
  (
    <MuiLogo
      {...el}
      name={el.name}
      label={el.label && (el.label)}
      onChange={el.onImageChange}
    />
  ),

  TextInput: (el) =>
  (
    <MuiInput
      {...el}
      name={el.name}
      label={el.label && (el.label)}
      fullWidth
      slotProps={{
        htmlInput: { maxLength: el.maxLength }
      }}
    />
  ),

  NumberInput: (el) =>
  (
    <MuiInput
      {...el}
      name={el.name}
      label={el.label && (el.label)}
      type='number'
      fullWidth
      slotProps={{
        htmlInput: { maxLength: el.maxLength, minValue: el.minValue, maxValue: el.maxValue },
        input: {
          sx: {
            /* Chrome, Safari, Edge, Opera */
            "&input::-webkit-outer-spin-button,input::-webkit-inner-spin-button": {
              "-webkit-appearance": "none",
              margin: 0,
            },

            /* Firefox */
            "&input[type=number]": {
              "-moz-appearance": "textfield"
            },
          }
        }
      }}
    />
  ),

  Password: (el) =>
  (
    <MuiInput
      {...el}
      name={el.name}
      label={el.label}
      type='password'
      fullWidth
      slotProps={{
        htmlInput: { maxLength: el.maxLength, minValue: el.minValue, maxValue: el.maxValue }
      }}
    />
  ),

  DatePicker: (el) =>
  (
    <MuiDatePicker
      {...el}
      name={el.name || "Date"}
      label={(el.label || "Date")}
      value={dayjs(el.value)}
      fullWidth
    />
  ),


  CheckBox: (el) =>
  (
    <MuiCheckbox
      {...el}
      name={el.name || "CheckBox"}
      label={(el.label || "CheckBox")}
    />
  ),

  Card: (el) =>
  (
    <Card variant='outlined' sx={{ padding: 2 }} >
      <Typography variant='h5' mb={3}>
        {el?.label && (el?.label)}
      </Typography>
      <Grid container spacing={2} columns={el?.columns} >
        {el?.elements?.map(el.formLoop)}
      </Grid>
    </Card>
  ),

  Accordian: (el) =>
  (
    <MuiAccordionArray
      accordionArray={el?.value || []}
      onClick={el?.onClick}
      {...el}
    />
  ),

  TreeList: (el) =>
  (
    <HorizontalTree
      getItems={el?.getItems}
      apiKey={el?.apiKey}
      apiRequest={el?.apiRequest}
      {...el}
    />
  ),

  Custom: (el) =>
    !!el && (el?.element || el?.name)
}